import { Machine } from 'xstate'
import { omit } from 'lodash'
import { LineMode } from 'common/Prose/utils/table'
import { MIN_CELL_WIDTH, MIN_CELL_HEIGHT, TABLE_RESIZE_SNAP_DELTA } from 'common/Prose/constants/tableConstants'

interface BorderMachineSchema {
  states: {
    idle: {}
    waitForDblClick: {}
    waiting: {}
    resizing: {}
  }
}

export interface BorderMachineContext {
  origin: number
  borderIdx: number
  fragmentIdx: number
  offset: number
  lineMode: LineMode
  originalSize: number
  atLowerLimit: boolean
}

export type BorderMachineEvents =
  | { type: 'MouseUp' }
  | {
      type: 'MouseDown'
      borderIdx: number
      fragmentIdx: number
      lineMode: LineMode
      origin: number
      originalSize: number
    }
  | { type: 'DblClick' }
  | {
      type: 'MouseMoveDown'
      offset: number
      columnWidths: number[]
      tableSize: number
      containerSize: number
      originalEvent: MouseEvent
    }
  | { type: 'MouseMoveUp' }

export const borderMachine = Machine<BorderMachineContext, BorderMachineSchema, BorderMachineEvents>({
  id: 'mouseM',
  initial: 'idle',
  context: {
    origin: 0,
    lineMode: LineMode.Column,
    borderIdx: 0,
    fragmentIdx: 0,
    offset: 0,
    originalSize: 0,
    atLowerLimit: false,
  },
  states: {
    idle: {
      on: {
        MouseDown: {
          target: 'waiting',
          actions: (ctx, evt) => {
            Object.assign(ctx, omit(evt, 'type'))
          },
        },
      },
    },
    waiting: {
      on: {
        MouseUp: 'waitForDblClick',
        MouseMoveDown: {
          target: 'resizing',
          cond: (ctx, evt) => {
            evt.originalEvent.preventDefault()
            return ctx.borderIdx > 0 && Math.abs(evt.offset) > 2
          },
          actions: (ctx, evt) => {
            Object.assign(ctx, omit(evt, 'type'))
          },
        },
        MouseMoveUp: 'idle',
      },
    },
    waitForDblClick: {
      on: {
        DblClick: {
          target: 'idle',
          actions: 'selectBorder',
        },
      },
      after: {
        500: 'idle',
      },
      entry: ['selectFragment'],
    },
    resizing: {
      on: {
        MouseUp: { target: 'idle', actions: 'resize' },
        MouseMoveUp: { target: 'idle' },
        MouseMoveDown: {
          actions: [
            // resizePreview
            (ctx, evt) => {
              evt.originalEvent.preventDefault()
              ctx.lineMode === LineMode.Row ? handleRowChange(ctx, evt.offset) : handleColumnChange(ctx, evt)
            },
          ],
        },
      },
    },
  },
})

const handleRowChange = (ctx: BorderMachineContext, offset: number) => {
  ctx.atLowerLimit = ctx.originalSize + offset < MIN_CELL_HEIGHT
  ctx.offset = Math.max(MIN_CELL_HEIGHT - ctx.originalSize, offset)
}

const handleColumnChange = (ctx: BorderMachineContext, evt: any) => {
  const { columnWidths, tableSize, containerSize, originalEvent } = evt
  const currentColumnWidth = columnWidths[ctx.borderIdx - 1]
  const columnAfterWidth = columnWidths[ctx.borderIdx]

  const finalColumn = columnWidths.length
  const widthDelta = originalEvent.clientX - ctx.origin
  if (ctx.borderIdx === finalColumn) {
    if (Math.abs(tableSize + widthDelta - containerSize) <= TABLE_RESIZE_SNAP_DELTA) {
      ctx.offset = containerSize - tableSize
    } else {
      // DEFAULT
      if (currentColumnWidth + widthDelta > MIN_CELL_WIDTH) {
        ctx.offset = widthDelta
        ctx.atLowerLimit = false
      } else {
        ctx.offset = MIN_CELL_WIDTH - currentColumnWidth
        ctx.atLowerLimit = true
      }
    }
  } else {
    // Need to take into account the width of the column after the dragged border
    if (currentColumnWidth + widthDelta > MIN_CELL_WIDTH && columnAfterWidth - widthDelta > MIN_CELL_WIDTH) {
      ctx.offset = widthDelta
      ctx.atLowerLimit = false
    } else if (currentColumnWidth + widthDelta < MIN_CELL_WIDTH) {
      ctx.offset = MIN_CELL_WIDTH - currentColumnWidth
      ctx.atLowerLimit = true
    } else {
      ctx.offset = columnAfterWidth - MIN_CELL_WIDTH
      ctx.atLowerLimit = true
    }
  }
}
